import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PushNotifications } from '@capacitor/push-notifications';
import cn from 'classnames';

import {
  useGetPushSettingsQuery,
  useUpdatePushSettingsMutation,
} from '@/api/queries/notifications';
import { playSound } from '@/common/lib/soundEffect';
import settingsCss from '@/components/Settings/SettingsSection/style.module.scss';

import css from '../changeLanguage/style.module.scss';

const PUSH_ALL = 'all';

// Backend push categories (PushCategory)
const CATEGORIES = ['contracts', 'storage', 'rewards', 'lottery', 'news'];

const Toggle: React.FC<{ on: boolean }> = ({ on }) => (
  <div
    className={cn(settingsCss.sliderChange, 'h-6', on && settingsCss.active)}
  >
    <div />
  </div>
);

/** Native app only: which pushes the player gets (iOS has the master switch). */
const PushSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data } = useGetPushSettingsQuery();
  const [updatePushSettings] = useUpdatePushSettingsMutation();
  const [disabled, setDisabled] = useState<string[]>([]);
  const [systemDenied, setSystemDenied] = useState(false);
  const categoryLabels: Record<string, string> = {
    contracts: t('Contract is about to expire'),
    storage: t('Storage is full'),
    rewards: t('Check-in reward is ready'),
    lottery: t('Lottery winnings'),
    news: t('News and messages from the team'),
  };

  useEffect(() => {
    if (data) setDisabled(data.disabled);
  }, [data]);

  useEffect(() => {
    PushNotifications.checkPermissions()
      .then(({ receive }) => setSystemDenied(receive === 'denied'))
      .catch(() => undefined);
  }, []);

  const save = (next: string[]): void => {
    playSound('buttons/switch');
    setDisabled(next);
    updatePushSettings({ disabled: next });
  };

  const toggle = (key: string): void =>
    save(
      disabled.includes(key)
        ? disabled.filter((el) => el !== key)
        : [...disabled, key],
    );

  const allOn = !disabled.includes(PUSH_ALL);
  const categories = data?.categories ?? CATEGORIES;

  return (
    <main className="page">
      <div style={{ height: '80vh', padding: '170px 10px 0' }}>
        <h2 style={{ textAlign: 'center' }}>{t('Push notifications')}</h2>
        {systemDenied && (
          <p className="text-center text-sm opacity-70 mt-3">
            {t(
              'Notifications are turned off in the phone settings. Turn them on in Settings → Star Industry → Notifications.',
            )}
          </p>
        )}
        <ul className={css.settingsList}>
          <li
            className={css.settingsListElement}
            onClick={() => toggle(PUSH_ALL)}
          >
            <span>{t('Push notifications')}</span>
            <Toggle on={allOn} />
          </li>
          {allOn &&
            categories.map((key) => (
              <li
                key={key}
                className={css.settingsListElement}
                onClick={() => toggle(key)}
              >
                <span>{categoryLabels[key] ?? key}</span>
                <Toggle on={!disabled.includes(key)} />
              </li>
            ))}
        </ul>
      </div>
    </main>
  );
};

export default PushSettings;
